/**
 * Vuetify 3 Theme Configuration - 2024-2025 Modern Approach
 * 
 * This file configures Vuetify's theme system with design tokens
 * and provides a consistent color palette across the application.
 */

// Define theme type inline since Vuetify 3 doesn't export ThemeDefinition
interface ThemeDefinition {
  dark: boolean
  colors: Record<string, string>
}

// Design token colors - these match our CSS custom properties
const colors = {
  // Primary palette (Custom Blue - RGB: 18, 90, 157)
  primary: {
    50: '#f0f4f8',
    100: '#d9e2ec',
    200: '#bcccdc',
    300: '#9fb3c8',
    400: '#829ab1',
    500: '#125A9D', // Main primary - RGB(18, 90, 157)
    600: '#0f4d87',
    700: '#0d4070',
    800: '#0a335a',
    900: '#082643',
  },
  
  // Secondary palette (Slate)
  secondary: {
    50: '#f8fafc',
    100: '#f1f5f9',
    200: '#e2e8f0',
    300: '#cbd5e1',
    400: '#94a3b8',
    500: '#64748b', // Main secondary
    600: '#475569',
    700: '#334155',
    800: '#1e293b',
    900: '#0f172a',
  },
  
  // Success palette (Green)
  success: {
    50: '#f0fdf4',
    100: '#dcfce7',
    200: '#bbf7d0',
    300: '#86efac',
    400: '#4ade80',
    500: '#22c55e', // Main success
    600: '#16a34a',
    700: '#15803d',
    800: '#166534',
    900: '#14532d',
  },
  
  // Warning palette (Amber)
  warning: {
    50: '#fffbeb',
    100: '#fef3c7',
    200: '#fde68a',
    300: '#fcd34d',
    400: '#fbbf24',
    500: '#f59e0b', // Main warning
    600: '#d97706',
    700: '#b45309',
    800: '#92400e',
    900: '#78350f',
  },
  
  // Error palette (Red)
  error: {
    50: '#fef2f2',
    100: '#fee2e2',
    200: '#fecaca',
    300: '#fca5a5',
    400: '#f87171',
    500: '#ef4444', // Main error
    600: '#dc2626',
    700: '#b91c1c',
    800: '#991b1b',
    900: '#7f1d1d',
  },
  
  // Info palette (Blue)
  info: {
    50: '#eff6ff',
    100: '#dbeafe',
    200: '#bfdbfe',
    300: '#93c5fd',
    400: '#60a5fa',
    500: '#3b82f6', // Main info (same as primary)
    600: '#2563eb',
    700: '#1d4ed8',
    800: '#1e40af',
    900: '#1e3a8a',
  }
}

// Light theme definition
const lightTheme: ThemeDefinition = {
  dark: false,
  colors: {
    // Main theme colors
    primary: colors.primary[500],
    'primary-lighten-5': colors.primary[50],
    'primary-lighten-4': colors.primary[100],
    'primary-lighten-3': colors.primary[200],
    'primary-lighten-2': colors.primary[300],
    'primary-lighten-1': colors.primary[400],
    'primary-darken-1': colors.primary[600],
    'primary-darken-2': colors.primary[700],
    'primary-darken-3': colors.primary[800],
    'primary-darken-4': colors.primary[900],
    
    secondary: colors.secondary[500],
    'secondary-lighten-5': colors.secondary[50],
    'secondary-lighten-4': colors.secondary[100],
    'secondary-lighten-3': colors.secondary[200],
    'secondary-lighten-2': colors.secondary[300],
    'secondary-lighten-1': colors.secondary[400],
    'secondary-darken-1': colors.secondary[600],
    'secondary-darken-2': colors.secondary[700],
    'secondary-darken-3': colors.secondary[800],
    'secondary-darken-4': colors.secondary[900],
    
    // Status colors
    success: colors.success[500],
    warning: colors.warning[500],
    error: colors.error[500],
    info: colors.info[500],
    
    // Surface colors
    background: '#ffffff',
    surface: '#ffffff',
    'surface-variant': colors.secondary[200],
    'surface-bright': '#ffffff',
    'surface-light': colors.secondary[50],
    
    // Text colors
    'on-background': colors.secondary[900],
    'on-surface': colors.secondary[900],
    'on-surface-variant': colors.secondary[600],
    'on-primary': '#ffffff',
    'on-secondary': '#ffffff',
    'on-success': '#ffffff',
    'on-warning': '#ffffff',
    'on-error': '#ffffff',
    'on-info': '#ffffff',
    
    // Additional utility colors
    outline: colors.secondary[300],
    'outline-variant': colors.secondary[200],
    shadow: colors.secondary[900],
    'surface-tint': colors.primary[500],
    'inverse-on-surface': colors.secondary[50],
    'inverse-surface': colors.secondary[800],
    'inverse-primary': colors.primary[200],
    'surface-container': colors.secondary[50],
  }
}

// Dark theme definition
const darkTheme: ThemeDefinition = {
  dark: true,
  colors: {
    // Main theme colors (adjusted for dark mode)
    primary: colors.primary[400],
    'primary-lighten-5': colors.primary[900],
    'primary-lighten-4': colors.primary[800],
    'primary-lighten-3': colors.primary[700],
    'primary-lighten-2': colors.primary[600],
    'primary-lighten-1': colors.primary[500],
    'primary-darken-1': colors.primary[300],
    'primary-darken-2': colors.primary[200],
    'primary-darken-3': colors.primary[100],
    'primary-darken-4': colors.primary[50],
    
    secondary: colors.secondary[400],
    'secondary-lighten-5': colors.secondary[900],
    'secondary-lighten-4': colors.secondary[800],
    'secondary-lighten-3': colors.secondary[700],
    'secondary-lighten-2': colors.secondary[600],
    'secondary-lighten-1': colors.secondary[500],
    'secondary-darken-1': colors.secondary[300],
    'secondary-darken-2': colors.secondary[200],
    'secondary-darken-3': colors.secondary[100],
    'secondary-darken-4': colors.secondary[50],
    
    // Status colors (slightly adjusted for dark mode)
    success: colors.success[400],
    warning: colors.warning[400],
    error: colors.error[400],
    info: colors.info[400],
    
    // Surface colors (dark mode)
    background: colors.secondary[900],
    surface: colors.secondary[800],
    'surface-variant': colors.secondary[700],
    'surface-bright': colors.secondary[700],
    'surface-light': colors.secondary[600],
    
    // Text colors (dark mode)
    'on-background': colors.secondary[50],
    'on-surface': colors.secondary[50],
    'on-surface-variant': colors.secondary[300],
    'on-primary': colors.secondary[900],
    'on-secondary': colors.secondary[900],
    'on-success': colors.secondary[900],
    'on-warning': colors.secondary[900],
    'on-error': colors.secondary[900],
    'on-info': colors.secondary[900],
    
    // Additional utility colors (dark mode)
    outline: colors.secondary[600],
    'outline-variant': colors.secondary[700],
    shadow: '#000000',
    'surface-tint': colors.primary[400],
    'inverse-on-surface': colors.secondary[800],
    'inverse-surface': colors.secondary[100],
    'inverse-primary': colors.primary[600],
    'surface-container': colors.secondary[800],
  }
}

// Export theme configurations
export const themes = {
  light: lightTheme,
  dark: darkTheme
}

// Export color palette for use in components
export { colors }

// Theme configuration for Vuetify
export const themeConfig = {
  defaultTheme: 'light',
  themes,
  variations: {
    colors: ['primary', 'secondary', 'success', 'warning', 'error', 'info'],
    lighten: 5,
    darken: 4,
  },
}

// CSS custom properties integration
export const cssVariables = {
  // Enable CSS variables for theme colors
  // This allows us to use rgb(var(--v-theme-primary)) in CSS
  prefix: '--v-',
  variables: {
    'theme-primary': 'primary',
    'theme-secondary': 'secondary',
    'theme-success': 'success',
    'theme-warning': 'warning',
    'theme-error': 'error',
    'theme-info': 'info',
    'theme-surface': 'surface',
    'theme-background': 'background',
    'theme-on-surface': 'on-surface',
    'theme-on-background': 'on-background',
  }
}
